import { LOCAL_IMAGE_MANIFEST } from './localImageManifest.generated'

/**
 * AssetsKit media lives in a separate Supabase project's public storage.
 * When that storage is unavailable (e.g. 402 quota restriction), media URLs
 * are mapped back to the matching file under public/ by original filename
 * (AssetsKit stores uploads as "<8-hex>-<original-name>").
 */

export const ASSETSKIT_STORAGE_HOST = 'cfbgcqqnezebauazjred.supabase.co'
export const ASSETSKIT_GENERIC_FALLBACK_IMAGE = '/kendrick-shoe-brand.jpg'

const STORAGE_PATH = '/storage/v1/'

const decodeSafely = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * Returns the AssetsKit storage URL behind a direct URL or a
 * `/.netlify/images?url=...` / `/_ipx/...` optimized URL, or null.
 */
export function extractAssetsKitStorageUrl(value: string | null | undefined): string | null {
  if (!value) return null
  const decoded = decodeSafely(value)
  if (!decoded.includes(ASSETSKIT_STORAGE_HOST) || !decoded.includes(STORAGE_PATH)) return null

  const start = decoded.indexOf(`https://${ASSETSKIT_STORAGE_HOST}`)
  if (start === -1) return null
  return decoded.slice(start).split(/[&\s,]/)[0]
}

export function isAssetsKitStorageUrl(value: unknown): value is string {
  return typeof value === 'string' && extractAssetsKitStorageUrl(value) !== null
}

/**
 * Local public path for an AssetsKit media URL, or null when no copy exists.
 */
export function resolveLocalAssetsKitImage(value: string | null | undefined): string | null {
  const storageUrl = extractAssetsKitStorageUrl(value)
  if (!storageUrl) return null

  const fileName = storageUrl.split('?')[0].split('/').pop() || ''
  const originalName = fileName.replace(/^[0-9a-f]{8}-/i, '').toLowerCase()
  return LOCAL_IMAGE_MANIFEST[originalName] || null
}

export function localAssetsKitImageOrGeneric(value: string): string {
  return resolveLocalAssetsKitImage(value) || ASSETSKIT_GENERIC_FALLBACK_IMAGE
}

/**
 * Deep-rewrites every AssetsKit storage URL (values and object keys, e.g.
 * mediaFocus maps) to local copies. Array entries without a local copy are
 * dropped; scalar fields fall back to a generic brand image.
 */
export function localizeAssetsKitMedia<T>(payload: T): T {
  const visit = (value: unknown): unknown => {
    if (typeof value === 'string') {
      return isAssetsKitStorageUrl(value) ? localAssetsKitImageOrGeneric(value) : value
    }

    if (Array.isArray(value)) {
      const next: unknown[] = []
      const seen = new Set<string>()
      for (const entry of value) {
        if (isAssetsKitStorageUrl(entry)) {
          const local = resolveLocalAssetsKitImage(entry)
          if (local && !seen.has(local)) {
            seen.add(local)
            next.push(local)
          }
          continue
        }
        next.push(visit(entry))
      }
      return next
    }

    if (value && typeof value === 'object') {
      const next: Record<string, unknown> = {}
      for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
        const nextKey = isAssetsKitStorageUrl(key) ? localAssetsKitImageOrGeneric(key) : key
        if (!(nextKey in next)) next[nextKey] = visit(entry)
      }
      return next
    }

    return value
  }

  return visit(payload) as T
}

export function findFirstAssetsKitStorageUrl(payload: unknown): string | null {
  if (typeof payload === 'string') return extractAssetsKitStorageUrl(payload)
  if (Array.isArray(payload)) {
    for (const entry of payload) {
      const found = findFirstAssetsKitStorageUrl(entry)
      if (found) return found
    }
    return null
  }
  if (payload && typeof payload === 'object') {
    for (const entry of Object.values(payload as Record<string, unknown>)) {
      const found = findFirstAssetsKitStorageUrl(entry)
      if (found) return found
    }
  }
  return null
}
